import { Box, Button, Flex, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import Download from '@/assets/icons/download.svg'
import External from '@/assets/icons/external.svg'
import LinkIcon from '@/assets/icons/link.svg'
import ResponsiveModal from '@/components/basic/ResponsiveModal'
import { CoinPrices } from '@/types/coin'
import clsx from '@/utils/clsx'

import styles from '../index.module.css'
import { useShareLinkInfo, useShareSocialTypes } from './hooks'
import ShareChart from './ShareChart'
import ShareDownloadHelper from './ShareDownloadHelper'
import ShareLion from './ShareLion'
import { shareLinkToTargetSocial, ShareType } from './utils'

interface ShareModalProps {
  opened: boolean
  onClose: () => void
  currentCoinPrices?: CoinPrices
  curTimeRange: string
  slug: string
}

export default function ShareModal({
  opened,
  onClose,
  currentCoinPrices,
  curTimeRange,
  slug,
}: ShareModalProps) {
  const [shareType, setShareType] = useState<ShareType>(ShareType.Chart)
  const [isDownloading, { open: startDownload, close: stopDownload }] = useDisclosure()
  const { tokenLink, popupName } = useShareLinkInfo(slug)
  const { socialType, setSocialType, displaySocialTypes } = useShareSocialTypes(shareType)
  const t = useTranslations('chart')
  const { buttonText, ButtonIcon, onClick } =
    shareType === ShareType.Link
      ? {
          buttonText: t('shareLink'),
          ButtonIcon: External,
          onClick: () => {
            shareLinkToTargetSocial({
              socialType,
              text: tokenLink,
              popupName,
            })
          },
        }
      : {
          buttonText: t('downloadImage'),
          ButtonIcon: Download,
          onClick: () => {
            startDownload()
          },
        }

  return (
    <ResponsiveModal
      title={<Text variant={'heading2'}>{t('share')}</Text>}
      opened={opened}
      onClose={onClose}
      size={480}
    >
      {isDownloading && (
        <ShareDownloadHelper
          socialType={socialType}
          shareType={shareType}
          curTimeRange={curTimeRange}
          currentCoinPrices={currentCoinPrices}
          onDownloadFinished={stopDownload}
        />
      )}
      <Stack>
        <Flex>
          {displaySocialTypes.map(({ Icon, value, label }) => (
            <Box
              key={value}
              onClick={() => setSocialType(value)}
              className={clsx({
                [styles.socialTypeItem]: true,
                [styles.socialTypeItemActive]: value === socialType,
              })}
            >
              <Icon />
              <Text variant={'subtitle-emphasis'}>{label}</Text>
            </Box>
          ))}
        </Flex>
        <Flex
          h={52}
          className={clsx({
            [styles.shareTypeItem]: true,
            [styles.shareTypeItemActive]: shareType === ShareType.Link,
          })}
          onClick={() => setShareType(ShareType.Link)}
        >
          <LinkIcon fill={'var(--content-secondary)'} />
          <Text variant={'body'} ms={2}>
            {t('linkToCoinPage')}
          </Text>
        </Flex>
        <Flex>
          <Box
            onClick={() => setShareType(ShareType.Chart)}
            className={clsx({
              [styles.shareTypeItem]: true,
              [styles.shareTypeItemActive]: shareType === ShareType.Chart,
            })}
            mr={16}
          >
            <ShareChart curTimeRange={curTimeRange} currentCoinPrices={currentCoinPrices} />
          </Box>
          <Box
            onClick={() => setShareType(ShareType.Lion)}
            className={clsx({
              [styles.shareTypeItem]: true,
              [styles.shareTypeItemActive]: shareType === ShareType.Lion,
            })}
          >
            <ShareLion />
          </Box>
        </Flex>
        <Button onClick={onClick} loading={isDownloading}>
          <Box mr={8}>
            <ButtonIcon width={20} height={20} />
          </Box>
          {buttonText}
        </Button>
      </Stack>
    </ResponsiveModal>
  )
}
